import React, { useState, useEffect } from 'react';
import { Apiary, Hive } from '../../types';
import { hiveService } from '../../services/firebase';
import { QuickAddHive } from './QuickAddHive';

interface HiveSelectionProps {
  selectedApiary: Apiary;
  onHiveSelect: (hive: Hive) => void;
  onCancel: () => void;
  onAddNewHive?: () => void;
}

export const HiveSelection: React.FC<HiveSelectionProps> = ({
  selectedApiary,
  onHiveSelect,
  onCancel,
  onAddNewHive
}) => {
  const [hives, setHives] = useState<Hive[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddHiveForm, setShowAddHiveForm] = useState(false);

  useEffect(() => {
    const loadHives = async () => {
      try {
        setLoading(true);
        setError(null);
        const apiaryHives = await hiveService.getApiaryHives(selectedApiary.id);
        setHives(apiaryHives);
      } catch (err) {
        console.error('Error loading hives:', err);
        setError('Failed to load hives');
      } finally {
        setLoading(false);
      }
    };

    loadHives();
  }, [selectedApiary.id]);

  const handleAddNewHive = () => {
    setShowAddHiveForm(true);
  };

  const handleHiveAdded = async () => {
    setShowAddHiveForm(false);
    // Reload hives
    try {
      setLoading(true);
      const apiaryHives = await hiveService.getApiaryHives(selectedApiary.id);
      setHives(apiaryHives);
    } catch (err) {
      console.error('Error reloading hives:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAddHive = () => {
    setShowAddHiveForm(false);
  };

  if (loading) {
    return (
      <div style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          maxWidth: '500px',
          width: '90%'
        }}>
          <h3>Loading hives...</h3>
          <div>Please wait while we fetch the hives for {selectedApiary.name}.</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          maxWidth: '500px',
          width: '90%'
        }}>
          <h3>Error</h3>
          <p>{error}</p>
          <button 
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto'
      }}>
        <h3 style={{ marginTop: 0 }}>
          🏡 Select Hive to Inspect
        </h3>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          Apiary: <strong>{selectedApiary.name}</strong> ({selectedApiary.location})
        </p>

        {hives.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h4 style={{ color: '#6c757d' }}>No Hives Found</h4>
            <p style={{ color: '#6c757d', marginBottom: '20px' }}>
              This apiary doesn't have any hives yet. Add a hive to start tracking inspections.
            </p>
            {onAddNewHive && (
              <button
                onClick={handleAddNewHive}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                ➕ Add New Hive
              </button>
            )}
          </div>
        ) : (
          <div style={{ marginBottom: '20px' }}>
            <p style={{ marginBottom: '15px', fontWeight: 'bold' }}>
              Choose a hive to inspect ({hives.length} hive{hives.length !== 1 ? 's' : ''} available):
            </p>
            
            <div style={{ 
              display: 'grid', 
              gap: '10px',
              maxHeight: '300px',
              overflowY: 'auto'
            }}>
              {hives.map((hive) => (
                <div
                  key={hive.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('🔴 HIVE CARD CLICKED:', hive.name);
                    onHiveSelect(hive);
                  }}
                  style={{
                    padding: '15px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backgroundColor: '#f8f9fa'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#007cba';
                    e.currentTarget.style.backgroundColor = '#e3f2fd';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e9ecef';
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h4 style={{ margin: '0 0 8px 0', color: '#007cba' }}>
                        🏺 {hive.name}
                      </h4>
                      <div style={{ fontSize: '0.9em', color: '#666' }}>
                        <div><strong>Established:</strong> {new Date(hive.established).toLocaleDateString()}</div>
                        {hive.queen_origin && (
                          <div><strong>Queen Origin:</strong> {hive.queen_origin}</div>
                        )}
                        {hive.queen_marked && (
                          <div>
                            <strong>Queen Marked:</strong> 
                            {hive.queen_marking_color && (
                              <span style={{ marginLeft: '5px' }}>
                                {hive.queen_marking_color === 'white' && '⚪ White'}
                                {hive.queen_marking_color === 'yellow' && '🟡 Yellow'}
                                {hive.queen_marking_color === 'red' && '🔴 Red'}
                                {hive.queen_marking_color === 'green' && '🟢 Green'}
                                {hive.queen_marking_color === 'blue' && '🔵 Blue'}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{
                      padding: '4px 8px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '0.8em',
                      fontWeight: 'bold'
                    }}>
                      🔍 Inspect
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {onAddNewHive && (
              <div style={{ marginTop: '15px', textAlign: 'center' }}>
                <button
                  onClick={handleAddNewHive}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  ➕ Add New Hive
                </button>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button 
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Quick Add Hive Modal */}
      {showAddHiveForm && (
        <QuickAddHive
          selectedApiary={selectedApiary}
          onHiveAdded={handleHiveAdded}
          onCancel={handleCancelAddHive}
        />
      )}
    </div>
  );
};
